import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Combobox, Input, Radio, type ComboboxProps } from '@node-space/ui-components'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { AddCustomerActions } from 'pages/EmbeddedCustomers/AddCustomer/AddCustomerActions'
import { CustomerEntityType } from 'types/customers'
import type { CreateCustomerAgreement } from 'types/customers'
import type { ErrorDetails } from 'types/types'
import { useAddCustomerAgreementForm } from '../hooks/useAddCustomerAgreementForm'

export interface AddCustomerAgreementFormProps {
  defaultValues?: CreateCustomerAgreement
  serverErrors?: ErrorDetails
  onConfirm: (
    data: CreateCustomerAgreement,
    displayData: Partial<Record<keyof CreateCustomerAgreement, string>>
  ) => void
  onCancel: () => void
}

export const AddCustomerAgreementForm = ({
  defaultValues,
  serverErrors,
  onConfirm,
  onCancel,
}: AddCustomerAgreementFormProps) => {
  const { t } = useTranslation()
  const { enableAddEmbeddedV2Individual } = useFeatureFlags()
  const { accountCapabilities } = useAccountsContext()
  const {
    isEmbeddedCustomerCompanyCapabilityEnabled,
    isEmbeddedCustomerIndividualCapabilityEnabled,
    isPartnerManagedCustomerCompanyCapabilityEnabled,
  } = accountCapabilities || {}

  const isCompanyCapabilityEnabled =
    isEmbeddedCustomerCompanyCapabilityEnabled || isPartnerManagedCustomerCompanyCapabilityEnabled
  const showIndividual =
    enableAddEmbeddedV2Individual && isEmbeddedCustomerIndividualCapabilityEnabled
  const showRadio = showIndividual && isCompanyCapabilityEnabled

  const [customerType, setCustomerType] = useState<string>(
    defaultValues?.customerType ??
      (!isCompanyCapabilityEnabled && showIndividual
        ? CustomerEntityType.INDIVIDUAL
        : CustomerEntityType.COMPANY)
  )

  const customerTypeOptions = [
    { value: CustomerEntityType.COMPANY, label: t('customers.business') },
    { value: CustomerEntityType.INDIVIDUAL, label: t('customers.individual') },
  ]

  const {
    formValues,
    hasErrors,
    handleSubmit,
    setInputField,
    setComboboxField,
    useCaseOptions,
    countryOptions,
    isFetchingCountries,
    buildDisplayData,
  } = useAddCustomerAgreementForm(defaultValues, serverErrors, customerType)

  const onSubmit = (data: CreateCustomerAgreement) => {
    onConfirm({ ...data, customerType }, buildDisplayData({ ...data, customerType }))
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box flexCol gap={16}>
        {showRadio && (
          <Radio
            value={customerType}
            onChange={setCustomerType}
            horizontal
            options={customerTypeOptions}
          />
        )}
        <Input {...setInputField({ key: 'customerName' })} testId="agreement-customer-name" />
        <Input {...setInputField({ key: 'reference' })} testId="agreement-reference" />
        <Combobox
          {...(setComboboxField({ key: 'useCase', items: useCaseOptions }) as ComboboxProps)}
          defaultValue={formValues.useCase}
          testId="agreement-use-case"
        />
        <Combobox
          {...(setComboboxField({ key: 'countryCode', items: countryOptions }) as ComboboxProps)}
          defaultValue={formValues.countryCode}
          loadingInline={isFetchingCountries}
          testId="agreement-country-code"
        />
      </Box>
      <AddCustomerActions onCancel={onCancel} isDisableContinue={hasErrors} />
    </form>
  )
}
